import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FirebaseError } from 'firebase/app';

const LoginPage: React.FC = () => {
  const { login, register, signInWithGoogle, loading, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = location.state?.from || '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle redirect when user becomes authenticated
  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || '/create';
      console.log('LoginPage - User authenticated, redirecting to:', from);
      navigate(from, { replace: true });
    }
  }, [user, navigate, location.state]);

  console.log('LoginPage - Rendering:', {
    hasUser: !!user,
    loading,
    isSubmitting,
    hasError: !!error,
    pathname: location.pathname,
    currentTime: new Date().toISOString()
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || isSubmitting) {
      console.log('LoginPage - Submit attempted while loading/submitting, aborting');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await login(email, password);
      // Navigation will be handled by the useEffect
    } catch (err) {
      console.error('Login error:', err);
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case 'auth/user-not-found':
          case 'auth/wrong-password':
            setError('Invalid email or password');
            break;
          case 'auth/invalid-email':
            setError('Please enter a valid email address');
            break;
          default:
            setError(`Authentication error: ${err.code}`);
        }
      } else {
        setError('An unexpected error occurred');
      }
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (loading || isSubmitting) {
      console.log('LoginPage - Google sign-in attempted while loading/submitting, aborting');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await signInWithGoogle();
      // Navigation will be handled by the useEffect
    } catch (err) {
      console.error('Google sign-in error:', err);
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case 'auth/popup-closed-by-user':
            setError('Sign in was cancelled');
            break;
          case 'auth/popup-blocked':
            setError('Please allow popups for this website');
            break;
          default:
            setError(`Failed to sign in with Google: ${err.code}`);
        }
      } else {
        setError('An unexpected error occurred');
      }
      setIsSubmitting(false);
    }
  };

  const isDisabled = loading || isSubmitting;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-dream-900 to-mist-900 px-4">
      <div className="max-w-md w-full p-8 space-y-8 bg-dream-800/50 backdrop-blur-sm rounded-lg shadow-xl">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-dream-50 mb-2 animate-drift">DriftRealm</h1>
          <p className="text-dream-300">Your personal memory sanctuary</p>
        </div>

        {error && (
          <div className="bg-red-500/20 text-red-200 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col space-y-4">
          <button
            onClick={handleGoogleSignIn}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-dream-700/50 hover:bg-dream-700/70
                     text-dream-50 rounded-lg transition-all duration-300 hover:scale-105
                     border border-dream-600/30 hover:border-dream-500/50
                     backdrop-blur-sm group disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isDisabled}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span>{isDisabled ? 'Please wait...' : 'Sign in with Google'}</span>
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-dream-700/30"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-dream-800/50 text-dream-300">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-dream-200 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 bg-dream-700/50 border border-dream-600/30 
                         focus:border-dream-500/50 rounded-lg shadow-sm text-dream-50 
                         placeholder-dream-400 focus:outline-none focus:ring-1 focus:ring-dream-500
                         disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter your email"
                required
                disabled={isDisabled}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-dream-200 mb-1">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 bg-dream-700/50 border border-dream-600/30 
                         focus:border-dream-500/50 rounded-lg shadow-sm text-dream-50 
                         placeholder-dream-400 focus:outline-none focus:ring-1 focus:ring-dream-500
                         disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder={isSignUp ? "Create a password (min. 6 characters)" : "Enter your password"}
                required
                disabled={isDisabled}
                minLength={6}
              />
            </div>

            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg
                       shadow-sm text-sm font-medium text-dream-50 bg-dream-600 hover:bg-dream-700
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dream-500
                       disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              disabled={isDisabled}
            >
              {isDisabled ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {isSignUp ? 'Creating account...' : 'Signing in...'}
                </span>
              ) : (
                isSignUp ? 'Sign Up' : 'Sign In'
              )}
            </button>
          </form>

          <div className="text-center">
            <button
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-dream-300 hover:text-dream-100 text-sm transition-colors duration-200
                       disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isDisabled}
            >
              {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 